import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import CpdTextField from '@/components/ui/CpdTextField';
import FacilityDropdown from '@/components/FacilityDropdown';

const SftpConnectionBasicInfoSection = forwardRef(
  (
    {
      organizationId,
      onFormChange = () => {},
      fieldErrors = {},
      isEdit = false,
      initialData = null,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
      alias_name: '',
      description: undefined,
      host: '',
      port: '',
      username: '',
      password: undefined,
      folders: undefined,
      type: undefined,
      facility_id: undefined,
      notification_email: undefined,
    });

    console.log('FORM DATA', formData);
    useEffect(() => {
      if (initialData) {
        setFormData({
          alias_name: initialData.alias_name,
          description: initialData.description,
          host: initialData.host,
          port: initialData.port,
          username: initialData.username,
          password: undefined, // INFO: Never populate password
          folders: initialData.folders || [],
          type: initialData.type,
          facility_id: initialData.facility_id,
          notification_email: initialData.notification_email,
        });
      }
    }, [initialData]);

    useEffect(() => {
      onFormChange(formData);
    }, [formData, onFormChange]);

    const handleFormChange = (field, value) => {
      const optionalStringFields = [
        'description',
        'password',
        'type',
        'facility_id',
        'notification_email',
      ];
      if (optionalStringFields.includes(field) && (!value || value === '')) {
        setFormData((prev) => ({ ...prev, [field]: undefined }));
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
    };

    const handleAddFolder = () => {
      setFormData((prev) => ({
        ...prev,
        folders: prev.folders ? [...prev.folders, ''] : [''],
      }));
    };

    const handleRemoveFolder = (index) => {
      setFormData((prev) => {
        const updatedFolders = prev.folders.filter((_, i) => i !== index);
        return {
          ...prev,
          folders: updatedFolders.length > 0 ? updatedFolders : undefined,
        };
      });
    };

    const handleFolderChange = (index, value) => {
      setFormData((prev) => ({
        ...prev,
        folders: prev.folders.map((folder, i) =>
          i === index ? value : folder,
        ),
      }));
    };

    useImperativeHandle(ref, () => ({
      formData: formData,
    }));

    return (
      <Box>
        <Typography sx={{ mb: 1.5 }} variant='h6' gutterBottom>
          Connection Information
        </Typography>

        <CpdTextField
          label={t('sftpConnections.form.aliasName')}
          value={formData.alias_name}
          onChange={(e) => handleFormChange('alias_name', e.target.value)}
          required
          autoComplete='off'
          error={!!fieldErrors.alias_name}
          helperText={
            fieldErrors.alias_name || t('sftpConnections.form.aliasNameHelper')
          }
        />

        <CpdTextField
          label={t('sftpConnections.form.description')}
          value={formData.description ?? ''}
          onChange={(e) => handleFormChange('description', e.target.value)}
          multiline
          rows={3}
          autoComplete='off'
          error={!!fieldErrors.description}
          helperText={
            fieldErrors.description ||
            t('sftpConnections.form.descriptionHelper')
          }
        />

        <Divider sx={{ mb: 3, mt: 3 }} />

        <Typography variant='h6' gutterBottom>
          Server Details
        </Typography>

        <CpdTextField
          label={t('sftpConnections.form.host')}
          value={formData.host}
          onChange={(e) => handleFormChange('host', e.target.value)}
          required
          placeholder='sftp.example.com'
          autoComplete='off'
          error={!!fieldErrors.host}
          helperText={fieldErrors.host || t('sftpConnections.form.hostHelper')}
        />

        <CpdTextField
          label={t('sftpConnections.form.port')}
          value={formData.port}
          onChange={(e) =>
            handleFormChange('port', parseInt(e.target.value, 10) || 22)
          }
          type='number'
          inputProps={{ min: 1, max: 65535 }}
          autoComplete='off'
          error={!!fieldErrors.port}
          helperText={fieldErrors.port || t('sftpConnections.form.portHelper')}
        />

        <Divider sx={{ mb: 3, mt: 3 }} />

        <Typography variant='h6' gutterBottom>
          Authentication
        </Typography>

        <CpdTextField
          label={t('sftpConnections.form.username')}
          value={formData.username}
          onChange={(e) => handleFormChange('username', e.target.value)}
          required
          autoComplete='username'
          error={!!fieldErrors.username}
          helperText={
            fieldErrors.username || t('sftpConnections.form.usernameHelper')
          }
        />

        <CpdTextField
          label={t('sftpConnections.form.password')}
          value={formData.password ?? ''}
          onChange={(e) => handleFormChange('password', e.target.value)}
          type='password'
          required={!isEdit}
          autoComplete='new-password'
          error={!!fieldErrors.password}
          helperText={
            fieldErrors.password || t('sftpConnections.form.passwordHelper')
          }
        />

        {isEdit && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 1, mb: 2 }}>
            Leave password blank to keep the current password
          </Typography>
        )}

        <Divider sx={{ mb: 3, mt: 3 }} />

        <Typography variant='h6' gutterBottom>
          Configuration
        </Typography>

        <FacilityDropdown
          label={t('sftpConnections.form.facility')}
          value={formData.facility_id}
          onChange={(e) => handleFormChange('facility_id', e.target.value)}
          error={!!fieldErrors.facility_id}
          helperText={
            fieldErrors.facility_id || t('sftpConnections.form.facilityHelper')
          }
          placeholderKey='sftpConnections.form.facilityHelper'
          useFormsApi={true}
          organizationId={organizationId}
        />

        <CpdTextField
          label={t('sftpConnections.form.type')}
          value={formData.type ?? ''}
          onChange={(e) => handleFormChange('type', e.target.value)}
          select
          error={!!fieldErrors.type}
          helperText={fieldErrors.type || t('sftpConnections.form.typeHelper')}>
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          <MenuItem value='facility_users'>
            {t('sftpConnections.filters.facilityUsers')}
          </MenuItem>
        </CpdTextField>

        <CpdTextField
          label={t('sftpConnections.form.notificationEmail')}
          value={formData.notification_email ?? ''}
          onChange={(e) =>
            handleFormChange('notification_email', e.target.value)
          }
          type='email'
          autoComplete='email'
          error={!!fieldErrors.notification_email}
          helperText={
            fieldErrors.notification_email ||
            t('sftpConnections.form.notificationEmailHelper')
          }
        />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant='subtitle1' sx={{ flexGrow: 1 }}>
              {t('sftpConnections.form.folders')}
            </Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              onClick={handleAddFolder}>
              {t('sftpConnections.form.addFolder')}
            </Button>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {t('sftpConnections.form.foldersHelper')}
          </Typography>
          {formData.folders?.map((folder, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CpdTextField
                value={folder}
                onChange={(e) => handleFolderChange(index, e.target.value)}
                placeholder='/incoming'
                autoComplete='off'
                error={!!fieldErrors[`folders[${index}]`]}
                helperText={fieldErrors[`folders[${index}]`]}
                sx={{ flexGrow: 1 }}
              />
              <IconButton
                onClick={() => handleRemoveFolder(index)}
                color='error'
                size='small'>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {fieldErrors.folders && (
            <Typography variant='caption' color='error' sx={{ mt: 0.5 }}>
              {fieldErrors.folders}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);

SftpConnectionBasicInfoSection.displayName = 'SftpConnectionBasicInfoSection';

export default SftpConnectionBasicInfoSection;
